import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { PortableText } from "@portabletext/react";
import { sanityClient } from "./sanityClient";
import "./BlogPost.css";
import Footer from "./Footer";

// Custom Portable Text components
// const portableTextComponents = {
//   types: {
//     image: ({ value }) => {
//       if (!value?.asset?.url) return null;
//       return (
//         <figure className="portable-image">
//           <img
//             src={value.asset.url}
//             alt={value.alt || "Blog image"}
//             loading="lazy"
//           />
//           {value.caption && <figcaption>{value.caption}</figcaption>}
//         </figure>
//       );
//     },
//   },
//   block: {
//     h2: ({ children }) => <h2 className="blog-h2">{children}</h2>,
//     h3: ({ children }) => <h3 className="blog-h3">{children}</h3>,
//     h4: ({ children }) => <h4 className="blog-h4">{children}</h4>,
//   },
// };

const portableTextComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?.url) return null;
      return (
        <figure className="portable-image">
          <img
            src={value.asset.url}
            alt={value.alt || "Blog image"}
            loading="lazy"
          />
          {value.caption && <figcaption>{value.caption}</figcaption>}
        </figure>
      );
    },
  },
  marks: {
    link: ({ children, value }) => {
      const href = value?.href || "";
      const isExternal = /^https?:\/\//.test(href);
      return (
        <a
          href={href}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
        >
          {children}
        </a>
      );
    },
  },
  block: {
    h2: ({ children }) => <h2 className="blog-h2">{children}</h2>,
    h3: ({ children }) => <h3 className="blog-h3">{children}</h3>,
    h4: ({ children }) => <h4 className="blog-h4">{children}</h4>,
  },
};

const getPortableTextPlainText = (value) => {
  if (!Array.isArray(value)) return "";

  return value
    .filter((block) => block._type === "block")
    .map(
      (block) =>
        block.children?.map((child) => child.text || "").join("") || "",
    )
    .join("\n")
    .trim();
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [showError, setShowError] = useState(false);

  const handleEmailSubmit = () => {
    if (email) {
      setShowError(false);

      const mailtoLink = `mailto:attoli@outlook.com?subject=${encodeURIComponent(
        "Excited to Connect!",
      )}&body=${encodeURIComponent(`Hi attoli Team,`)}`;
      window.location.href = mailtoLink;

      setEmail("");
    } else {
      setShowError(true);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (showError) {
      setShowError(false);
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const query = `*[_type == "post" && slug.current == $slug][0] {
          _id,
          title,
          slug,
          publishedAt,
          description,
          featuredImage{asset->{url}, alt, caption},
          body[] {
            ...,
            _type == "image" => {
              ...,
              asset-> {
                url
              }
            }
          },
          author,
          categories
        }`;
        const data = await sanityClient.fetch(query, { slug });
        if (!data) {
          setError("Post not found");
        } else {
          setPost(data);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching post:", err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <main className="blog-post-page">
        <div className="wrap">
          <p>Loading...</p>
        </div>
      </main>
    );
  }

  if (error || !post) {
    return (
      <main className="blog-post-page">
        <div className="wrap">
          <p style={{ color: "red" }}>Error: {error || "Post not found"}</p>
          <Link to="/resources">Back to Resources</Link>
        </div>
      </main>
    );
  }

  const descriptionText = getPortableTextPlainText(post.description);

  return (
    <main className="blog-post-page">
      <Helmet>
        <title>{post.title} | Attoli</title>
        <meta name="description" content={descriptionText || post.title} />
        <link rel="canonical" href={`https://attoli.com/content/${slug}`} />
      </Helmet>

      <article className="blog-post">
        <div className="wrap">
          <Link to="/resources" className="back-link">
            <span className="blog-svg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="50"
                height="20"
                viewBox="0 0 38 15"
                fill="none"
              >
                <path
                  fill="#2b4a6f"
                  d="M10 7.519l-.939-.344h0l.939.344zm14.386-1.205l-.981-.192.981.192zm1.276 5.509l.537.843.148-.094.107-.139-.792-.611zm4.819-4.304l-.385-.923h0l.385.923zm7.227.707a1 1 0 0 0 0-1.414L31.343.448a1 1 0 0 0-1.414 0 1 1 0 0 0 0 1.414l5.657 5.657-5.657 5.657a1 1 0 0 0 1.414 1.414l6.364-6.364zM1 7.519l.554.833.029-.019.094-.061.361-.23 1.277-.77c1.054-.609 2.397-1.32 3.629-1.787.617-.234 1.17-.392 1.623-.455.477-.066.707-.008.788.034.025.013.031.021.039.034a.56.56 0 0 1 .058.235c.029.327-.047.906-.39 1.842l1.878.689c.383-1.044.571-1.949.505-2.705-.072-.815-.45-1.493-1.16-1.865-.627-.329-1.358-.332-1.993-.244-.659.092-1.367.305-2.056.566-1.381.523-2.833 1.297-3.921 1.925l-1.341.808-.385.245-.104.068-.028.018c-.011.007-.011.007.543.84zm8.061-.344c-.198.54-.328 1.038-.36 1.484-.032.441.024.94.325 1.364.319.45.786.64 1.21.697.403.054.824-.001 1.21-.09.775-.179 1.694-.566 2.633-1.014l3.023-1.554c2.115-1.122 4.107-2.168 5.476-2.524.329-.086.573-.117.742-.115s.195.038.161.014c-.15-.105.085-.139-.076.685l1.963.384c.192-.98.152-2.083-.74-2.707-.405-.283-.868-.37-1.28-.376s-.849.069-1.274.179c-1.65.43-3.888 1.621-5.909 2.693l-2.948 1.517c-.92.439-1.673.743-2.221.87-.276.064-.429.065-.492.057-.043-.006.066.003.155.127.07.099.024.131.038-.063.014-.187.078-.49.243-.94l-1.878-.689zm14.343-1.053c-.361 1.844-.474 3.185-.413 4.161.059.95.294 1.72.811 2.215.567.544 1.242.546 1.664.459a2.34 2.34 0 0 0 .502-.167l.15-.076.049-.028.018-.011c.013-.008.013-.008-.524-.852l-.536-.844.019-.012c-.038.018-.064.027-.084.032-.037.008.053-.013.125.056.021.02-.151-.135-.198-.895-.046-.734.034-1.887.38-3.652l-1.963-.384zm2.257 5.701l.791.611.024-.031.08-.101.311-.377 1.093-1.213c.922-.954 2.005-1.894 2.904-2.27l-.771-1.846c-1.31.547-2.637 1.758-3.572 2.725l-1.184 1.314-.341.414-.093.117-.025.032c-.01.013-.01.013.781.624zm5.204-3.381c.989-.413 1.791-.42 2.697-.307.871.108 2.083.385 3.437.385v-2c-1.197 0-2.041-.226-3.19-.369-1.114-.139-2.297-.146-3.715.447l.771 1.846z"
                ></path>
              </svg>
            </span>{" "}
            Back to Resources
          </Link>

          <header className="post-header">
            {post.featuredImage?.asset?.url && (
              <figure className="featured-image">
                <img
                  src={post.featuredImage.asset.url}
                  alt={post.featuredImage.alt || post.title}
                />
                {post.featuredImage.caption && (
                  <figcaption>{post.featuredImage.caption}</figcaption>
                )}
              </figure>
            )}
            <h1>{post.title}</h1>
            {post.publishedAt && (
              <time className="post-date">
                {new Date(post.publishedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            )}
            {post.author && <p className="post-author">By {post.author}</p>}
          </header>

          {post.description && (
            <div className="post-excerpt">
              <PortableText
                value={post.description}
                components={portableTextComponents}
              />
            </div>
          )}

          <div className="post-content">
            {post.body ? (
              <PortableText
                value={post.body}
                components={portableTextComponents}
              />
            ) : (
              <p>No content available for this post yet.</p>
            )}
          </div>
        </div>
      </article>
      <section className="blog-contact-section">
        <div className="blog-contact-content">
          <h2 className="blog-contact-title">Reach Out For Any Questions</h2>

          <div className="blog-email-form">
            {showError && (
              <div className="blog-error-message">
                Please enter your email address first.
              </div>
            )}

            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email address"
              className="blog-email-input"
            />
            <button onClick={handleEmailSubmit} className="blog-contact-btn">
              Contact
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default BlogPost;
